import React, { useEffect, useCallback, useRef, useState, memo } from 'react';
import { View, LayoutChangeEvent } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useBoundStore } from '@/services/zustand/Store';
import 'keen-slider/keen-slider.min.css';
import '@/components/styles/webKeenSlider.css';
import { useKeenSlider, KeenSliderPlugin } from 'keen-slider/react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useHandleSelectArticle } from '@/services/PDF/useHandleSelectArticle';
import { pageFiles } from '@/services/datatypes/pdf';

const MutationPlugin: KeenSliderPlugin = (slider) => {
    const observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            slider.update()
        })
    });
    const config = { childList: true };
    slider.on("created", () => {
        observer.observe(slider.container, config)
    });
    slider.on("destroyed", () => {
        observer.disconnect()
    });
};

const origin = window.location.origin;

pdfjs.GlobalWorkerOptions.workerSrc = `${origin}/pdf.worker.min.js`;

const options = {
    cMapUrl: `${origin}/cmaps/`,
    standardFontDataUrl: `${origin}/fonts/`,
    enableHWA: true,
    httpHeaders: {
        "x-api-key": "d85ed5ba-b8f9-459a-b60d-37863f696a7e"
    }
}

const Arrow = (props: {
    disabled: boolean
    left?: boolean
    onClick: (e: any) => void
}) => {
    const disabled = props.disabled ? " arrow--disabled" : ""
    return (
        <svg
            onClick={props.onClick}
            className={`arrow ${props.left ? "arrow--left" : "arrow--right"
                } ${disabled}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
        >
            {props.left && (
                <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
            )}
            {!props.left && (
                <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
            )}
        </svg>
    );
};

const SliderControlComponent = (props: {
    instanceRef: any,
    numPages: any,
}) => {

    const currentPage: number = useBoundStore(useCallback((state) => state.currentPage, []));
    return (
        <>
            <Arrow
                left
                onClick={(e: any) =>
                    e.stopPropagation() || props.instanceRef.current?.prev()
                }
                disabled={(currentPage - 1) === 0}
            />
            <Arrow
                onClick={(e: any) =>
                    e.stopPropagation() || props.instanceRef.current?.next()
                }
                disabled={
                    (currentPage - 1) === (props.numPages - 1)
                }
            />
        </>
    );
};

const MemoizedDocument = memo(function MemoizedDocument(props: {
    file: string,
    pageNumber: number,
    width: any,
    height: any,
    onGetAnnotationsSuccess: (annotations: any) => void,
}) {
    return (
        <View
            style={{
                width: props.width,
                height: props.height,
            }}
        >
            <View
                style={{
                    flex: 1,
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: props.width,
                    height: props.height,
                }}
            >
                <Document file={props.file} options={options}>
                    <Page
                        pageNumber={1}
                        height={props.height}
                        onGetAnnotationsSuccess={props.onGetAnnotationsSuccess}
                    />
                </Document>
            </View>
        </View>
    );
});

export default function ViewerComponent(props: { magazineDataForViewer: pageFiles[] }) {
    const currentMagazine = useBoundStore(useCallback((state) => state.magazine, []));
    const interstitialData = useBoundStore(useCallback((state) => state.interstitialData, []));
    const currentPage = useBoundStore(useCallback((state) => state.currentPage, []));
    const updateCurrentPage = useBoundStore(useCallback((state) => state.updateCurrentPage, []));
    const handleSelectArticle = useHandleSelectArticle();
    const refContainer = useRef();
    const [visibleWidth, setVisibleWidth] = useState(0);
    const [visibleHeight, setVisibleHeight] = useState(0);
    const visibleWidthRef = useRef<any>(0);
    const visibleHeightRef = useRef<any>(0);
    const [sliderRef, instanceRef] = useKeenSlider({
        slides: {
            origin: 'center',
            perView: 1,
            spacing: 0,
        },
        renderMode: 'performance',
        initial: 0,
        slideChanged(slider) {
            updateCurrentPage(slider.track.details.rel + 1);
        },
    }, [MutationPlugin]);

    useEffect(() => {
        //console.log("ViewerComponent.web.tsx - useEffect - currentPage:", currentPage);
        //console.log("ViewerComponent.web.tsx - useEffect - instanceRef.current?.track.details.rel:", instanceRef.current?.track.details.rel);
        //console.log("ViewerComponent.web.tsx - useEffect - visiblePage:", visiblePage);
        if (!instanceRef.current || !instanceRef.current.track?.details) return;
        if (currentPage - 1 !== instanceRef.current?.track.details.rel) {
            instanceRef.current?.moveToIdx(currentPage);
        }
    }, [currentPage])
    let pdfFiles = [];
    if (props.magazineDataForViewer) {
        const magazineDataForViewerLength = props.magazineDataForViewer.length;
        for (let a = 0; a < magazineDataForViewerLength; a++) {
            pdfFiles.push(
                `${origin}/protected-files/${currentMagazine?.date}/${currentMagazine?.edition}/${props.magazineDataForViewer[a].page}/page.pdf`
            );
        }
    }
    const handleLayout = (event: LayoutChangeEvent) => {
        const { width, height } = event.nativeEvent.layout;
        visibleWidthRef.current = width;
        visibleHeightRef.current = height;
        if (width != visibleWidth || height != visibleHeight) {
            setTimeout(() => {
                setVisibleWidth(width);
                setVisibleHeight(height);
            }, 100);
        }
    };

    const handleLinkClick = (event: any) => {
        event.preventDefault();
        const uri = event.target.href;

        if (typeof uri !== 'undefined') {
            if (uri.startsWith('http://article-')) {
                const articleID = uri.replace('http://article-', '').replace('/', '');
                handleSelectArticle(articleID, currentMagazine?.date, currentMagazine?.edition);
            }
            else {
                if (interstitialData !== null) {
                    //trackMatomoInterstitial(`Interstitial`, "Klick", interstitialData.id)
                    //trackMatomoBrowsing(`Interstitial://${magazine.date}/${interstitialData.id}/Klick`)
                    //Linking.openURL(interstitialData.url);
                }
                const newWindow = window.open(uri, '_blank', 'noopener,noreferrer')
                if (newWindow) {
                    newWindow.opener = null;
                }
            }
        }
    };

    const onGetAnnotationsSuccess = async (annotations: any) => {
        setTimeout(() => {
            for (const annotation of annotations) {
                const link = document.querySelectorAll("[data-annotation-id='" + annotation.id + "']");

                if (link.length > 0) {
                    link[0].addEventListener('click', handleLinkClick);
                }
                else {
                    console.error("Couldn't find annotation with id: " + annotation.id);
                }
            }
        }, 250);
    };

    const renderPDF = (pdf: string, index: number) => {
        const isVisible = true;
        return (
            <div
                className={`keen-slider__slide number-slide${index + 1}`}
                key={`page_slide_${index + 1}`}
                style={{
                    background: 'grey',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: visibleHeightRef.current,
                    maxHeight: visibleHeightRef.current,
                    width: visibleWidthRef.current,
                    maxWidth: visibleWidthRef.current,
                }}
            >
                {isVisible ? (
                    <MemoizedDocument
                        file={pdf}
                        pageNumber={index + 1}
                        width={visibleWidthRef.current}
                        height={visibleHeightRef.current}
                        onGetAnnotationsSuccess={onGetAnnotationsSuccess}
                    />
                ) : (
                    <div>Loading...</div>
                )}
            </div>
        );
    }

    useFocusEffect(
        React.useCallback(() => {
            return () => {
                //console.log('ViewerComponent focus effect cleanup');
                instanceRef.current?.moveToIdx(0);
            };
        }, [])
    );

    useEffect(() => {
        instanceRef.current?.update();
    }, [visibleWidth, visibleHeight]);

    return (
        <View
            style={{
                flex: 1,
                justifyContent: 'flex-start',
                flexDirection: 'row',
                width: '100%',
            }}
            onLayout={handleLayout}
            //@ts-ignore 
            ref={refContainer}
        >
            <div
                className="navigation-wrapper"
                style={{
                    width: visibleWidthRef.current,
                    maxWidth: visibleWidthRef.current,
                    height: visibleHeightRef.current,
                    margin: '0 auto'
                }}
            >
                <div ref={sliderRef} className="keen-slider">
                    {
                        //props.pdfFiles.map((pdf: string, index: number) => renderPDF(pdf, index))
                        pdfFiles.map((pdf: string, index: number) => renderPDF(pdf, index))
                    }
                </div>
                <>
                    <SliderControlComponent
                        instanceRef={instanceRef}
                        numPages={currentMagazine?.numberOfPages}
                    />
                </>
            </div>
        </View>
    );
}